import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { Spinner } from '@/components/ui';

import { useAuth } from './providers/authContext';

export interface RequireAuthProps {
  /** Rendered when authenticated. Falls back to the nested route `<Outlet />`. */
  children?: ReactNode | undefined;
  redirectTo?: string | undefined;
}

/**
 * Route guard. The intended destination is preserved in the navigation state so the login screen
 * can return the user exactly where they were trying to go instead of dumping them on the home
 * route — the difference between 2 and 5 taps on a repeat purchase (RNF08).
 */
export function RequireAuth({ children, redirectTo = '/login' }: RequireAuthProps) {
  const { status } = useAuth();
  const location = useLocation();

  if (status === 'loading') {
    return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: '50dvh' }} aria-busy="true">
        <Spinner size="lg" label="Verificando sua sessão…" />
      </div>
    );
  }

  if (status === 'unauthenticated') {
    return <Navigate to={redirectTo} replace state={{ from: location }} />;
  }

  return <>{children ?? <Outlet />}</>;
}
